const IMAGE_LOAD_TIMEOUT_MS = 3000;
const IFRAME_SCRIPT_TIMEOUT_MS = 5000;
const FETCH_TIMEOUT_MS = 5000;

const ADSENSE_SCRIPT_URL = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
const ACCEPTABLE_ADS_TEST_URLS = Object.freeze([
  'https://ad-delivery.net/px.gif?ch=1',
  'https://ad-delivery.net/px.gif?ch=2',
]);

/**
 * Detects cosmetic filters by creating ad-like elements and checking if they're hidden.
 * Returns true if any ad-targeting class name causes the element to be hidden.
 */
async function detectCosmeticFilter() {
  let testElement = null;
  
  try {
    testElement = document.createElement('ins');
    testElement.id = 'ad-unit';
    // Position off-screen to avoid visual impact while testing
    testElement.style.cssText = 'display:inline-block !important; width:300px !important; height:250px !important; position: absolute !important; top: -10000px !important; left: -10000px !important; visibility: visible !important; opacity: 1 !important;';
    document.body.appendChild(testElement);

    // Common class names that adblockers target with cosmetic filters
    const AD_TARGET_CLASS_NAMES = Object.freeze([
      'ad',
      'ad01',
      'ad-unit',
      'adv-space',
      'adsbygoogle',
      'banner_ad',
      'banner-ad',
      'content_ad',
      'content-ad',
      'side_ad',
      'pub_300x250',
      'pub_300x250m',
      'pub_728x90',
      'text-ad',
      'textAd',
      'text_ad',
      'text_ads',
      'text-ads',
      'text-ad-links',
    ]);
    for (const className of AD_TARGET_CLASS_NAMES) {
      testElement.className = className;
      // Brief delay to allow adblockers to process the element
      await new Promise((resolve) => setTimeout(resolve, 1));
      
      const style = window.getComputedStyle(testElement);
      // Check if element is hidden by adblocker's cosmetic filters
      if (style.display === 'none' || 
          style.visibility === 'hidden' ||
          style.opacity === '0' ||
          style.height === '0px' ||
          style.width === '0px') {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  } finally {
    testElement?.parentNode?.removeChild(testElement);
  }
}

/**
 * Detects snippet filters by checking if XMLHttpRequest has been modified by adblockers.
 * uBlock Origin modifies the constructor with specific markers.
 */
function detectSnippetFilter() {
  try {
    const xhrString = XMLHttpRequest.toString();
    return xhrString.includes('uBO:') && xhrString.includes('class extends self.XMLHttpRequest');
  } catch {
    return false;
  }
}

/**
 * Checks if a network request to an ad-related URL is blocked.
 * @param {string} targetUrl - URL to test
 * @param {string[]} requiredSubstrings - Strings that must be in response body
 * @param {string[]} forbiddenSubstrings - Strings that must NOT be in response body
 * @returns {Promise<boolean>} true if request is blocked
 */
async function isRequestBlocked(targetUrl, requiredSubstrings = [], forbiddenSubstrings = []) {
  const controller = new AbortController();
  let timeoutId;
  
  try {
    timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const response = await fetch(targetUrl, {
      signal: controller.signal,
      cache: 'no-cache',
    });
    
    clearTimeout(timeoutId);
    
    // Check for redirects, empty responses, or error status codes
    if (response.redirected || 
        (response.body === null && response.type === 'default') ||
        response.status < 200 || response.status >= 300) {
      return true;
    }
    
    const body = await response.text();
    
    if (body.length === 0) {
      return true;
    }
    
    // Validate response content
    if (requiredSubstrings.length > 0 && !requiredSubstrings.every(s => body.includes(s))) {
      return true;
    }
    
    if (forbiddenSubstrings.length > 0 && forbiddenSubstrings.some(s => body.includes(s))) {
      return true;
    }
    
    return false;
  } catch {
    clearTimeout(timeoutId);
    return true;
  }
}

/**
 * Detects if AdSense script is blocked by loading it in an isolated iframe.
 * Verifies the script loaded correctly by checking for expected properties.
 */
async function detectAdsenseBlocked() {
  return new Promise((resolve) => {
    let resolved = false;
    let timeoutId;
    let iframe;
    
    const finish = (blocked) => {
      if (resolved) return;
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (iframe?.parentNode) iframe.remove();
      resolve(blocked);
    };
    
    try {
      iframe = document.createElement('iframe');
      iframe.style.cssText = 'width:0;height:0;visibility:hidden;border:none;';
      document.head.appendChild(iframe);
      
      const win = iframe.contentWindow;
      if (!win) {
        return finish(true);
      }
      
      win.document.open();
      win.document.write('<html><head></head><body></body></html>');
      win.document.close();
      
      const script = win.document.createElement('script');
      script.src = ADSENSE_SCRIPT_URL;
      
      script.addEventListener('load', () => {
        try {
          const adsbygoogle = win.adsbygoogle;
          if (!adsbygoogle) {
            return finish(true);
          }
          
          // Verify it's the real AdSense script, not a blocked/redirected version
          const descriptor = Object.getOwnPropertyDescriptor(adsbygoogle, 'requestNonPersonalizedAds');
          finish(!descriptor?.set);
        } catch {
          finish(true);
        }
      }, { once: true });
      
      script.addEventListener('error', () => finish(true), { once: true });
      win.document.head.appendChild(script);
      
      timeoutId = setTimeout(() => finish(true), IFRAME_SCRIPT_TIMEOUT_MS);
    } catch {
      finish(true);
    }
  });
}

/**
 * Detects request filters by testing network requests to ad-related URLs.
 * Uses both fetch and iframe-based detection methods.
 */
async function detectRequestFilter() {
  const results = await Promise.all([
    isRequestBlocked(ADSENSE_SCRIPT_URL, ['adsbygoogle'], ['uBlock Origin']),
    detectAdsenseBlocked(),
  ]);
  return results.some(r => r === true);
}

/**
 * Checks if Acceptable Ads are enabled by testing two URLs.
 * If Acceptable Ads is working, one URL should be blocked and one allowed.
 * @param {string[]} testUrls - Array of exactly 2 URLs to test
 * @returns {Promise<boolean>} true if Acceptable Ads is blocked (both URLs have same result)
 */
async function detectAcceptableAdsBlocked(testUrls = ACCEPTABLE_ADS_TEST_URLS) {
  if (!Array.isArray(testUrls) || testUrls.length !== 2) {
    throw new Error('testUrls must be an array of exactly 2 URLs');
  }

  const results = await Promise.all(
    testUrls.map(async (url) => {
      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          let timeoutId;
          let resolved = false;
          
          const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
            resolved = true;
          };
          
          img.addEventListener('load', () => {
            if (!resolved) {
              cleanup();
              resolve();
            }
          }, { once: true });
          
          img.addEventListener('error', () => {
            if (!resolved) {
              cleanup();
              reject(new Error('Image load failed'));
            }
          }, { once: true });
          
          img.src = url;
          timeoutId = setTimeout(() => {
            if (!resolved) {
              cleanup();
              reject(new Error('Timeout'));
            }
          }, IMAGE_LOAD_TIMEOUT_MS);
        });
        return true;
      } catch {
        return false;
      }
    })
  );
  
  // If both URLs have the same result, Acceptable Ads is not working
  return results[0] === results[1];
}

/**
 * Main detection function that checks all adblock detection methods.
 * @param {Object} options - Optional configuration
 * @param {string[]} options.acceptableAdsTestUrls - Optional array of two URLs for Acceptable Ads detection
 * @returns {Promise<{isAdBlocked: boolean, isAcceptableAdsBlocked: boolean}>}
 */
export async function detectAdblock(options = {}) {
  const { acceptableAdsTestUrls } = options;
  
  // Run all detection methods in parallel
  const [cosmetic, snippet, request] = await Promise.all([
    detectCosmeticFilter(),
    Promise.resolve(detectSnippetFilter()),
    detectRequestFilter(),
  ]);
  
  const isAdblockActive = cosmetic || snippet || request;
  
  // Only check Acceptable Ads if adblock is detected
  const isAcceptableAdsBlocked = isAdblockActive
    ? await detectAcceptableAdsBlocked(acceptableAdsTestUrls)
    : false;
  
  return {
    isAdBlocked: isAdblockActive,
    isAcceptableAdsBlocked: isAcceptableAdsBlocked,
  };
}
